/** Hammer truck GPS deep links — https://www.hammerapp.com/integrate-us
 *
 * Note: Hammer docs show the bare domain (hammerapp.com), but Android App Links
 * are only verified for the `www` host (the bare domain 301-redirects, which
 * Android does not follow during verification). Using `www.hammerapp.com` keeps
 * the link opening in the installed app on Android.
 */

export const HAMMER_ROUTE_BASE = 'https://www.hammerapp.com/app/route';

/** Max waypoints per Hammer URL (including depot / leg start). */
export const HAMMER_MAX_WAYPOINTS = 5;

/**
 * Encode one waypoint for the path: "lat,lng" or a street address.
 * @param {{ latitude?: *, longitude?: *, address_text?: string, label?: string }} point
 */
export function formatHammerWaypoint(point) {
  if (!point) return null;
  const lat = point.latitude != null ? Number(point.latitude) : NaN;
  const lng = point.longitude != null ? Number(point.longitude) : NaN;
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `${lat},${lng}`;
  }
  const addr = (point.address_text || '').trim();
  if (addr) return addr;
  const label = (point.label || '').trim();
  return label || null;
}

/**
 * Encode a waypoint for the URL path, preserving Hammer-friendly characters.
 * Hammer parses path segments split by '/', so we must escape '/', '?', '#', '%',
 * and space, but MUST keep ',' literal (used inside lat,lng and inside addresses
 * like "City, ST"). encodeURIComponent escapes commas, which Hammer can't parse.
 */
function encodeWaypointForHammer(s) {
  return String(s)
    .replace(/%/g, '%25')
    .replace(/\//g, '%2F')
    .replace(/\?/g, '%3F')
    .replace(/#/g, '%23')
    .replace(/&/g, '%26')
    .replace(/\s+/g, '%20');
}

/**
 * @param {Array<string|null>} waypoints
 * @returns {string|null}
 */
export function buildHammerRouteUrl(waypoints) {
  const parts = (waypoints || []).map((w) => (typeof w === 'string' ? w : formatHammerWaypoint(w))).filter(Boolean);
  if (!parts.length) return null;
  if (parts.length > HAMMER_MAX_WAYPOINTS) return null;
  const path = parts.map((p) => encodeWaypointForHammer(p)).join('/');
  return `${HAMMER_ROUTE_BASE}/${path}`;
}

/**
 * Split depot + ordered stops into legs of at most 5 waypoints (overlapping last/first between legs).
 * @param {{ latitude?: *, longitude?: *, address_text?: string, name?: string }} depot
 * @param {Array<{ latitude?: *, longitude?: *, address_text?: string, label?: string, sequence?: number }>} stops
 */
export function buildHammerLegs(depot, stops) {
  const ordered = [...(stops || [])].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  const depotWp = depot
    ? {
        latitude: depot.latitude,
        longitude: depot.longitude,
        address_text: depot.address_text || depot.name,
        label: depot.name,
      }
    : null;
  const depotStr = formatHammerWaypoint(depotWp);
  if (!depotStr && !ordered.length) return [];

  const points = [];
  if (depotWp) points.push(depotWp);
  for (const s of ordered) {
    points.push({
      latitude: s.latitude,
      longitude: s.longitude,
      address_text: s.address_text,
      label: s.label,
    });
  }

  if (!points.length) return [];

  const legs = [];
  let start = 0;
  let legIndex = 0;
  while (start < points.length) {
    const chunk = points.slice(start, start + HAMMER_MAX_WAYPOINTS);
    const encoded = chunk.map((p) => formatHammerWaypoint(p)).filter(Boolean);
    const url = buildHammerRouteUrl(encoded);
    if (!url) break;

    const deliveriesInLeg = start === 0 ? Math.max(0, chunk.length - 1) : Math.max(0, chunk.length - 1);
    const viaLabel =
      start === 0
        ? deliveriesInLeg
          ? `depot + ${deliveriesInLeg} stop${deliveriesInLeg === 1 ? '' : 's'}`
          : 'depot'
        : `continues · ${deliveriesInLeg} stop${deliveriesInLeg === 1 ? '' : 's'}`;

    legs.push({
      index: legIndex + 1,
      url,
      waypointCount: encoded.length,
      viaLabel,
    });

    legIndex += 1;
    if (start + HAMMER_MAX_WAYPOINTS >= points.length) break;
    start += HAMMER_MAX_WAYPOINTS - 1;
  }

  return legs;
}

/**
 * Single leg: default depot → one delivery (manifest row).
 */
export function buildHammerManifestUrl(depot, manifest) {
  const deliveryLine = [
    manifest.delivery_address,
    manifest.delivery_city,
    manifest.delivery_state,
    manifest.delivery_zip_code,
  ]
    .filter(Boolean)
    .join(', ');
  const delivery = {
    // DB/API exposes `latitude`/`longitude` on manifests (not `delivery_latitude`/`delivery_longitude`).
    latitude: manifest.latitude ?? manifest.delivery_latitude,
    longitude: manifest.longitude ?? manifest.delivery_longitude,
    address_text: deliveryLine,
    label: manifest.consignee_name || manifest.manifest_number,
  };
  const depotWp = depot
    ? {
        latitude: depot.latitude,
        longitude: depot.longitude,
        address_text: [depot.address_line1, depot.city, depot.state, depot.zip_code].filter(Boolean).join(', '),
        label: depot.name,
      }
    : null;
  return buildHammerRouteUrl(
    [formatHammerWaypoint(depotWp), formatHammerWaypoint(delivery)].filter(Boolean)
  );
}
